import { useState, useCallback, useRef } from 'react';
import { useConversation } from '@elevenlabs/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { Phone, PhoneOff, Mic, MicOff, Loader2, Save, PhoneOutgoing } from 'lucide-react';

const API_BASE = `${window.location.origin}/api`;

function formatDuration(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

async function logCallActivity(leadId, duration, status, notes, callType) {
  const token = localStorage.getItem('token');
  let desc = `${callType === 'outbound' ? 'Outbound' : 'Browser'} AI Call — ${formatDuration(duration)} (${status})`;
  if (notes?.trim()) desc += `\nNotes: ${notes.trim()}`;
  try {
    await fetch(`${API_BASE}/crm/leads/${leadId}/activities`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ activity_type: 'call', description: desc }),
    });
    return true;
  } catch (err) {
    console.error('Failed to log call activity:', err);
    return false;
  }
}

export default function AICallDialog({ open, onOpenChange, lead, onCallLogged }) {
  const [connecting, setConnecting] = useState(false);
  const [muted, setMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [phase, setPhase] = useState('idle'); // idle | calling | outbound | notes
  const [callNotes, setCallNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [callType, setCallType] = useState(null); // 'browser' | 'outbound'
  const [outboundInfo, setOutboundInfo] = useState(null);
  const callStartRef = useRef(null);
  const timerRef = useRef(null);
  const finalDurationRef = useRef(0);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    callStartRef.current = Date.now();
    setCallDuration(0);
    timerRef.current = setInterval(() => {
      setCallDuration(Math.floor((Date.now() - callStartRef.current) / 1000));
    }, 1000);
  }, []);

  const resetState = useCallback(() => {
    stopTimer();
    setConnecting(false);
    setCallDuration(0);
    setMuted(false);
    setPhase('idle');
    setCallNotes('');
    setSaving(false);
    setCallType(null);
    setOutboundInfo(null);
    callStartRef.current = null;
    finalDurationRef.current = 0;
  }, [stopTimer]);

  // WebRTC browser conversation hooks
  const conversation = useConversation({
    onConnect: () => {
      setConnecting(false);
      setPhase('calling');
      startTimer();
      toast.success(`Connected — AI calling ${lead?.name}`);
    },
    onDisconnect: () => {
      stopTimer();
      const duration = callStartRef.current ? Math.floor((Date.now() - callStartRef.current) / 1000) : 0;
      finalDurationRef.current = duration;
      callStartRef.current = null;
      if (duration > 0) {
        setPhase('notes');
        setCallNotes('');
      } else {
        toast.info('Call ended');
        setPhase('idle');
      }
    },
    onError: (error) => {
      setConnecting(false);
      stopTimer();
      const duration = callStartRef.current ? Math.floor((Date.now() - callStartRef.current) / 1000) : 0;
      if (lead?.id && duration > 0) {
        finalDurationRef.current = duration;
        setPhase('notes');
        setCallNotes('');
      } else {
        callStartRef.current = null;
        setPhase('idle');
      }
      console.error('Conversation error:', error);
      toast.error('Call error: ' + (error?.message || 'Connection failed'));
    },
  });

  // ── Outbound phone call (via SIP trunk) ──
  const startOutboundCall = useCallback(async () => {
    setConnecting(true);
    setCallType('outbound');
    try {
      const token = localStorage.getItem('token');

      // Build description
      const descParts = [
        lead?.company && `Company: ${lead.company}`,
        lead?.source && `Source: ${lead.source}`,
        lead?.status && `Status: ${lead.status}`,
        lead?.remarks && `Remarks: ${lead.remarks}`,
      ].filter(Boolean).join('. ');

      const res = await fetch(`${API_BASE}/crm/outbound-call`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          to_number: lead.phone,
          lead_name: lead.name,
          lead_company: lead.company || '',
          lead_description: descParts || 'CRM lead',
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to initiate outbound call');
      }

      const data = await res.json();
      setOutboundInfo(data);
      setConnecting(false);
      setPhase('outbound');
      startTimer();
      toast.success(`Call initiated to ${lead.phone}`);
    } catch (err) {
      setConnecting(false);
      console.error('Outbound call error:', err);
      toast.error(err.message || 'Failed to start outbound call');
    }
  }, [lead, startTimer]);

  // ── Browser WebRTC call ──
  const startBrowserCall = useCallback(async () => {
    setConnecting(true);
    setCallType('browser');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/crm/generate-call-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to get call token');
      }
      const { signed_url } = await res.json();
      await navigator.mediaDevices.getUserMedia({ audio: true });

      const firstName = (lead?.name || '').split(' ')[0] || 'there';
      const salutation = firstName !== 'there' ? `Mr./Ms. ${firstName}` : 'Hello';
      const descParts = [
        lead?.company && `Company: ${lead.company}`,
        lead?.source && `Source: ${lead.source}`,
        lead?.status && `Status: ${lead.status}`,
        lead?.remarks && `Remarks: ${lead.remarks}`,
      ].filter(Boolean).join('. ');

      await conversation.startSession({
        signedUrl: signed_url,
        overrides: {
          conversation: {
            dynamicVariables: {
              lead_salutation: salutation,
              lead_first_name: firstName,
              lead_description: descParts || 'New lead',
            },
          },
        },
      });
    } catch (err) {
      setConnecting(false);
      console.error('Start call error:', err);
      toast.error(err.message || 'Failed to start call');
    }
  }, [conversation, lead]);

  const endBrowserCall = useCallback(async () => {
    try { await conversation.endSession(); } catch (_) { /* */ }
  }, [conversation]);

  const endOutboundCall = useCallback(() => {
    stopTimer();
    const duration = callStartRef.current ? Math.floor((Date.now() - callStartRef.current) / 1000) : 0;
    finalDurationRef.current = duration;
    callStartRef.current = null;
    setPhase('notes');
    setCallNotes('');
  }, [stopTimer]);

  const toggleMute = useCallback(() => {
    if (muted) { conversation.setVolume({ volume: 1 }); }
    else { conversation.setVolume({ volume: 0 }); }
    setMuted(!muted);
  }, [conversation, muted]);

  const handleSaveNotes = useCallback(async () => {
    setSaving(true);
    const ok = await logCallActivity(lead?.id, finalDurationRef.current, 'completed', callNotes, callType || 'browser');
    setSaving(false);
    if (ok) { toast.success('Call activity logged'); onCallLogged?.(); }
    else { toast.error('Failed to save call activity'); }
    resetState();
    onOpenChange(false);
  }, [lead, callNotes, callType, onCallLogged, onOpenChange, resetState]);

  const handleSkipNotes = useCallback(async () => {
    setSaving(true);
    await logCallActivity(lead?.id, finalDurationRef.current, 'completed', '', callType || 'browser');
    setSaving(false);
    onCallLogged?.();
    resetState();
    onOpenChange(false);
  }, [lead, callType, onCallLogged, onOpenChange, resetState]);

  const handleClose = useCallback(() => {
    if (conversation.status === 'connected') { conversation.endSession(); return; }
    if (phase === 'notes') return;
    resetState();
    onOpenChange(false);
  }, [conversation, phase, resetState, onOpenChange]);

  const isConnected = conversation.status === 'connected';
  const hasPhone = !!(lead?.phone);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-sm" data-testid="ai-call-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="w-5 h-5" />
            {phase === 'notes' ? 'Call Summary' : phase === 'outbound' ? 'Outbound Call' : 'AI Voice Call'}
          </DialogTitle>
          <DialogDescription>
            {phase === 'notes' ? 'Add notes about this call before saving'
              : phase === 'outbound' ? `Calling ${lead?.phone}...`
              : 'Call the lead via phone or browser'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-4">
          {/* Lead Info */}
          <div className="text-center space-y-1">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto text-primary text-lg font-bold">
              {lead?.name?.charAt(0)?.toUpperCase() || '?'}
            </div>
            <p className="font-semibold text-lg" data-testid="call-lead-name">{lead?.name || 'Unknown'}</p>
            <p className="text-sm text-muted-foreground">{lead?.company || ''}</p>
            {lead?.phone && <p className="text-sm text-muted-foreground">{lead.phone}</p>}
          </div>

          {/* ── Post-call notes phase ── */}
          {phase === 'notes' && (
            <div className="space-y-4">
              <div className="flex justify-center">
                <Badge variant="outline" className="text-base px-3 py-1 font-mono" data-testid="call-final-duration">
                  {formatDuration(finalDurationRef.current)}
                </Badge>
              </div>
              <Textarea
                placeholder="What did you discuss? Any next steps?"
                value={callNotes}
                onChange={(e) => setCallNotes(e.target.value)}
                rows={3}
                autoFocus
                data-testid="call-notes-input"
              />
              <div className="flex gap-2">
                <Button className="flex-1" onClick={handleSaveNotes} disabled={saving} data-testid="save-call-notes-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                  Save &amp; Close
                </Button>
                <Button variant="ghost" onClick={handleSkipNotes} disabled={saving} data-testid="skip-call-notes-btn">
                  Skip
                </Button>
              </div>
            </div>
          )}

          {/* ── Outbound call in progress ── */}
          {phase === 'outbound' && (
            <div className="space-y-4">
              <div className="flex flex-col items-center gap-2">
                <Badge className="bg-green-600 animate-pulse" data-testid="call-status-badge">
                  Outbound Call Active
                </Badge>
                <span className="text-lg font-mono font-semibold tabular-nums" data-testid="call-duration">
                  {formatDuration(callDuration)}
                </span>
                <p className="text-xs text-muted-foreground">AI agent is speaking with {lead?.name} on {lead?.phone}</p>
              </div>
              <div className="flex justify-center">
                <Button
                  size="lg"
                  variant="destructive"
                  className="rounded-full w-14 h-14"
                  onClick={endOutboundCall}
                  data-testid="end-call-btn"
                >
                  <PhoneOff className="w-6 h-6" />
                </Button>
              </div>
            </div>
          )}

          {/* ── Idle / Browser calling phase ── */}
          {phase !== 'notes' && phase !== 'outbound' && (
            <>
              {/* AI Context - shows dynamic variables being sent */}
              {phase === 'idle' && (
                <div className="rounded-lg bg-muted/50 border px-3 py-2.5 text-xs space-y-1" data-testid="ai-context-preview">
                  <p className="font-semibold text-muted-foreground uppercase tracking-wide text-[10px] mb-1.5">AI Context</p>
                  <div className="flex gap-1.5">
                    <span className="text-muted-foreground shrink-0">Salutation:</span>
                    <span className="font-medium" data-testid="ctx-salutation">{(lead?.name || '').split(' ')[0] ? `Mr./Ms. ${(lead?.name || '').split(' ')[0]}` : 'Hello'}</span>
                  </div>
                  <div className="flex gap-1.5">
                    <span className="text-muted-foreground shrink-0">First Name:</span>
                    <span className="font-medium" data-testid="ctx-first-name">{(lead?.name || '').split(' ')[0] || 'there'}</span>
                  </div>
                  <div className="flex gap-1.5">
                    <span className="text-muted-foreground shrink-0">Description:</span>
                    <span className="font-medium line-clamp-2" data-testid="ctx-description">
                      {[
                        lead?.company && `Company: ${lead.company}`,
                        lead?.source && `Source: ${lead.source}`,
                        lead?.status && `Status: ${lead.status}`,
                        lead?.remarks && `Remarks: ${lead.remarks}`,
                      ].filter(Boolean).join('. ') || 'No details'}
                    </span>
                  </div>
                </div>
              )}

              {/* Status & Timer */}
              <div className="flex flex-col items-center gap-2">
                <Badge
                  variant={isConnected ? 'default' : connecting ? 'outline' : 'secondary'}
                  className={isConnected ? 'bg-green-600 animate-pulse' : ''}
                  data-testid="call-status-badge"
                >
                  {isConnected ? 'Browser Call Connected' : connecting ? 'Connecting...' : 'Ready'}
                </Badge>
                {isConnected && (
                  <span className="text-lg font-mono font-semibold tabular-nums" data-testid="call-duration">
                    {formatDuration(callDuration)}
                  </span>
                )}
              </div>

              {/* Speaking Indicator */}
              {isConnected && (
                <div className="flex justify-center">
                  <div className={`flex items-center gap-2 text-sm ${conversation.isSpeaking ? 'text-green-600' : 'text-muted-foreground'}`}>
                    <div className={`w-3 h-3 rounded-full ${conversation.isSpeaking ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} />
                    {conversation.isSpeaking ? 'AI is speaking...' : 'Listening...'}
                  </div>
                </div>
              )}

              {/* Controls */}
              <div className="flex flex-col items-center gap-3">
                {!isConnected && !connecting && (
                  <div className="flex gap-3">
                    {hasPhone && (
                      <Button
                        size="lg"
                        className="rounded-full px-6 h-12 bg-green-600 hover:bg-green-700 gap-2"
                        onClick={startOutboundCall}
                        data-testid="outbound-call-btn"
                      >
                        <PhoneOutgoing className="w-5 h-5" />
                        Call {lead?.phone}
                      </Button>
                    )}
                    <Button
                      size="lg"
                      variant="outline"
                      className="rounded-full px-6 h-12 gap-2"
                      onClick={startBrowserCall}
                      data-testid="start-call-btn"
                    >
                      <Mic className="w-5 h-5" />
                      Browser
                    </Button>
                  </div>
                )}

                {!hasPhone && !isConnected && !connecting && (
                  <p className="text-xs text-muted-foreground">No phone number — browser call only</p>
                )}

                {connecting && (
                  <Button size="lg" className="rounded-full w-14 h-14" disabled>
                    <Loader2 className="w-6 h-6 animate-spin" />
                  </Button>
                )}

                {isConnected && (
                  <div className="flex gap-4">
                    <Button
                      size="lg"
                      variant={muted ? 'destructive' : 'outline'}
                      className="rounded-full w-14 h-14"
                      onClick={toggleMute}
                      data-testid="mute-btn"
                    >
                      {muted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
                    </Button>
                    <Button
                      size="lg"
                      variant="destructive"
                      className="rounded-full w-14 h-14"
                      onClick={endBrowserCall}
                      data-testid="end-call-btn"
                    >
                      <PhoneOff className="w-6 h-6" />
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
